import { useRef, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import { format, isAfter, isBefore, isValid, parse } from 'date-fns';
import { CopyCheckIcon, CopyIcon, PlusIcon, RotateCcwIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import * as yup from 'yup';

import { FormUnsavedConfirmationWrapper } from '../../../components/pages/backoffice/form-unsaved-confirmation-wrapper.jsx';
import CancelButton from '../../../components/buttons/cancel-button.jsx';
import Loading from '../../../components/feedback/loading.jsx';
import Heading from '../../../components/typography/heading.jsx';
import { Button } from '../../../components/ui/button.jsx';
import { Checkbox } from '../../../components/ui/checkbox.jsx';
import { DateTimeInputWithDatePicker } from '../../../components/ui/date-time-picker.jsx';
import FormCard from '../../../components/ui/form-card.jsx';
import FormFooter from '../../../components/ui/form-footer.jsx';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../components/ui/form.jsx';
import { InputCurrency } from '../../../components/ui/input-currency.jsx';
import { Input } from '../../../components/ui/input.jsx';
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectEmpty,
  MultiSelectList,
  MultiSelectSearch,
  MultiSelectTrigger,
  MultiSelectValue,
  renderMultiSelectOptions,
} from '../../../components/ui/multi-select.jsx';
import RichTextEditor from '../../../components/ui/rich-text-editor.jsx';
import { Textarea } from '../../../components/ui/textarea.jsx';
import { msCatalystApi } from '../../../services/api/ms-catalyst-api.js';
import { useClipboard } from '../../../hooks/use-clipboard.jsx';
import { chunk } from '../../../utils/arr.js';
import { cn } from '../../../utils/cn.js';
import {
  CAMPAIGN_BANNER_DIMENSIONS,
  CAMPAIGN_INACTIVE_HERO_SM_DIMENSIONS,
  CAMPAIGN_INACTIVE_HERO_XL_DIMENSIONS,
  CLIENT_URL,
  DATETIME_FORMAT,
} from '../../../utils/constants.js';
import { formatDateTimeToISO } from '../../../utils/format.js';
import { REGEX_DATETIME_EU } from '../../../utils/regex.js';
import { campaignUrl } from '../../../utils/urls.js';

const DEFAULT_MIN_ENTRY_AMOUNT = 25;
const DEFAULT_ADHERENT_SHOPS_VISIBLE = true;

const initalFormValues = {
  name: '',
  startDate: '',
  closeDate: '',
  endDate: '',
  numOfWinners: 3,
  numOfSubstituteWinners: 3,
  minEntryAmount: DEFAULT_MIN_ENTRY_AMOUNT,
  prizes: '',
  adherentShops: [],
  legalPhrase: '',
  description: '',
  regulationFile: '',
  regulationFilename: '',
  rules: '',
  link: CLIENT_URL,
  bannerFile: '',
  bannerFilename: '',
  heroNotActiveSmFile: '',
  heroNotActiveSmFilename: '',
  heroNotActiveXlFile: '',
  heroNotActiveXlFilename: '',
  adherentShopsVisible: DEFAULT_ADHERENT_SHOPS_VISIBLE,
};

export function CampaignsCreate() {
  const validBannerImageExtensions = [
    'jpg',
    'gif',
    'png',
    'jpeg',
    'svg',
    'webp',
  ];

  const validRegulationFileExtensions = ['pdf'];

  const navigate = useNavigate();
  const [multiselectShopsLoading, setMultiselectShopsLoading] = useState(false);
  const [multiselectShopsOptions, setMultiselectShopsOptions] = useState([]);

  const indexRef = useRef(0);
  const clipboard = useClipboard({ timeout: 2000 });

  const formCreateCampaign = useForm({
    mode: 'onChange',
    values: {
      ...initalFormValues,
    },
    resolver: yupResolver(
      yup.object().shape({
        name: yup
          .string()
          .min(10, 'Nome da Campanha deve ter pelo menos 10 caracteres')
          .required('Nome da Campanha é um campo obrigatório'),
        startDate: yup
          .string()
          .required('Data de Início é um campo obrigatório')
          .matches(
            REGEX_DATETIME_EU,
            'Data de Início possui um formato inválido',
          )
          .test('validDate', 'Insira uma data válida', function (value) {
            try {
              const parsedDate = parse(
                value,
                'dd-MM-yyyy HH:mm:ss',
                new Date(),
              );
              const isValidDate = isValid(parsedDate);

              return isValidDate;
            } catch (error) {
              return false;
            }
          })
          .test(
            'isBeforeEndDate',
            'Data de Início deve ser anterior à Data de Fecho de Participações',
            function (value) {
              const { closeDate } = this.parent;
              if (!value || !closeDate) {
                return true; // If one of the dates is missing, skip this validation
              }

              const parsedStartDate = parse(value, DATETIME_FORMAT, new Date());
              const parsedCloseDate = parse(
                closeDate,
                DATETIME_FORMAT,
                new Date(),
              );

              if (!isValid(parsedStartDate) || !isValid(parsedCloseDate)) {
                return true; // Skip this validation if dates are invalid
              }

              // Check if startDate is before to closeDate
              return isBefore(parsedStartDate, parsedCloseDate);
            },
          ),
        closeDate: yup
          .string()
          .required('Data de Fecho é um campo obrigatório')
          .matches(
            REGEX_DATETIME_EU,
            'Data de Fecho possui um formato inválido',
          )
          .test('validDate', 'Insira uma data válida', function (value) {
            try {
              const parsedDate = parse(
                value,
                'dd-MM-yyyy HH:mm:ss',
                new Date(),
              );
              const isValidDate = isValid(parsedDate);

              return isValidDate;
            } catch (error) {
              return false;
            }
          })
          .test(
            'isAfterStartDate',
            'Data de Fecho deve ser posterior à Data de Início',
            function (value) {
              const { startDate } = this.parent;
              if (!value || !startDate) {
                return true; // If one of the dates is missing, skip this validation
              }

              const parsedCloseDate = parse(
                value,
                'dd-MM-yyyy HH:mm:ss',
                new Date(),
              );
              const parsedStartDate = parse(
                startDate,
                'dd-MM-yyyy HH:mm:ss',
                new Date(),
              );

              if (!isValid(parsedStartDate) || !isValid(parsedCloseDate)) {
                return true; // Skip this validation if dates are invalid
              }

              // Check if endDate is after to startDate
              return isAfter(parsedCloseDate, parsedStartDate);
            },
          ),
        endDate: yup
          .string()
          .required('Data de Fim é um campo obrigatório')
          .matches(REGEX_DATETIME_EU, 'Data de Fim possui um formato inválido')
          .test('validDate', 'Insira uma data válida', function (value) {
            try {
              const parsedDate = parse(
                value,
                'dd-MM-yyyy HH:mm:ss',
                new Date(),
              );
              const isValidDate = isValid(parsedDate);

              return isValidDate;
            } catch (error) {
              return false;
            }
          })
          .test(
            'isAfterCloseDate',
            'Data de Fim deve ser posterior à Data de Fecho',
            function (value) {
              const { closeDate } = this.parent;
              if (!value || !closeDate) {
                return true; // If one of the dates is missing, skip this validation
              }

              const parsedEndDate = parse(
                value,
                'dd-MM-yyyy HH:mm:ss',
                new Date(),
              );
              const parsedCloseDate = parse(
                closeDate,
                'dd-MM-yyyy HH:mm:ss',
                new Date(),
              );

              if (!isValid(parsedCloseDate) || !isValid(parsedCloseDate)) {
                return true; // Skip this validation if dates are invalid
              }

              // Check if endDate is after to startDate
              return isAfter(parsedEndDate, parsedCloseDate);
            },
          ),
        numOfWinners: yup
          .number()
          .positive('Número de Vencedores tem de ser um número positivo')
          .min(1, 'Número de Vencedores tem de ser igual ou maior que 1')
          .required('Número de Vencedores é um campo obrigatório'),
        numOfSubstituteWinners: yup
          .number()
          .positive('Número de Suplentes tem de ser um número positivo')
          .min(1, 'Número de Suplentes tem de ser igual ou maior que 1')
          .required('Número de Suplentes é um campo obrigatório'),
        minEntryAmount: yup
          .number()
          .positive(
            'Valor Minimo para Participação tem de ser um número positivo',
          )
          .min(1, 'Valor Minimo para Participação tem de ser maior que 1')
          .required('Valor Minimo para Participação é um campo obrigatório'),
        description: yup
          .string()
          .max(
            '2000',
            'Condições da Campanha não pode ter mais do 500 caracteres',
          )
          .min('20', 'Condições da Campanha deve ter pelo menos 20 caracteres')
          .required('Condições da Campanha é um campo obrigatório'),
        regulationFile: yup
          .mixed()
          .test(
            'required',
            'Regulamento (ficheiro) é um campo obrigatório',
            (file) => {
              if (file && file.size) return true;
              return false;
            },
          )
          .test(
            'fileType',
            'O ficheiro inserido não é válido (tipo de ficheiro válido: pdf)',
            (value) => {
              return (
                value &&
                validRegulationFileExtensions.indexOf(
                  value.name.toLowerCase().split('.').pop(),
                ) > -1
              );
            },
          ),
        rules: yup
          .string()
          .max('700', 'Regulamento não pode ter mais do 700 caracteres')
          .min('20', 'Regulamento deve ter pelo menos 20 caracteres')
          .required('Regulamento é um campo obrigatório'),

        adherentShops: yup
          .array()
          .min(1, 'É requirido selecionar pelo menos uma Loja Aderente'),
        bannerFile: yup
          .mixed()
          .test('required', 'Banner é um campo obrigatório', (file) => {
            if (file && file.size) return true;
            return false;
          })
          .test(
            'fileType',
            'O ficheiro inserido não é uma imagem (tipo de imagens válidas: jpg, png, jpeg e webp)',
            (value) => {
              return (
                value &&
                validBannerImageExtensions.indexOf(
                  value.name.toLowerCase().split('.').pop(),
                ) > -1
              );
            },
          ),
        heroNotActiveSmFile: yup
          .mixed()
          .test(
            'fileType',
            'O ficheiro inserido não é uma imagem (tipo de imagens válidas: jpg, png, jpeg e webp)',
            (value) => {
              if (value && value.size) {
                return (
                  value &&
                  validBannerImageExtensions.indexOf(
                    value.name.toLowerCase().split('.').pop(),
                  ) > -1
                );
              }
            },
          ),
        heroNotActiveXlFile: yup
          .mixed()
          .test(
            'fileType',
            'O ficheiro inserido não é uma imagem (tipo de imagens válidas: jpg, png, jpeg e webp)',
            (value) => {
              if (value && value.size) {
                return (
                  value &&
                  validBannerImageExtensions.indexOf(
                    value.name.toLowerCase().split('.').pop(),
                  ) > -1
                );
              }
            },
          ),
      }),
    ),
  });

  const queryShops = useQuery({
    queryKey: ['adherentShops'],
    queryFn: async () => {
      const { data: shops } = await msCatalystApi.get(`/shops/all?status=true`);

      const shopsData = shops.data.map((item) => ({
        label: item.name,
        value: item.ROWID,
      }));

      setMultiselectShopsOptions(shopsData);

      return shopsData;
    },
  });

  const mutationCheckActiveCampaign = useMutation({
    mutationFn: async (data) => {
      const activeCampaign = await msCatalystApi.get('/campaigns/ongoing');

      return activeCampaign.data;
    },
  });

  const mutationCreateCampaign = useMutation({
    mutationFn: async (data) => {
      const formData = new FormData();
      formData.append('regulationFile', data.regulationFile);
      formData.append('bannerFile', data.bannerFile);

      if (data?.heroNotActiveSmFile) {
        formData.append('heroNotActiveSmFile', data.heroNotActiveSmFile);
      }

      if (data?.heroNotActiveXlFile) {
        formData.append('heroNotActiveXlFile', data.heroNotActiveXlFile);
      }

      const newData = { ...data };

      delete newData.bannerFile;
      delete newData.regulationFile;
      delete newData.heroNotActiveSmFile;
      delete newData.heroNotActiveXlFile;

      newData.startDate = formatDateTimeToISO(data.startDate);
      newData.closeDate = formatDateTimeToISO(data.closeDate);
      newData.endDate = formatDateTimeToISO(data.endDate);

      formData.append('data', JSON.stringify(newData));

      const createCampaign = await msCatalystApi.post('/campaigns', formData);

      return createCampaign.data;
    },
  });

  const mutationCreateCampaignAdherentShops = useMutation({
    mutationFn: async (data) => {
      const createCampaignAdherentShop = await msCatalystApi.post(
        '/campaigns/adherent-shops',
        data,
      );

      return createCampaignAdherentShop.data;
    },
  });

  const searchShops = async (items, keyword) => {
    if (!keyword) {
      setMultiselectShopsOptions(
        queryShops?.data.map((item) => ({
          label: item.name,
          value: item.ROWID,
        })),
      );
    }

    const lowerKeyword = keyword.toLowerCase();
    const filtered = items.filter((item) =>
      item.label.toLowerCase().includes(lowerKeyword),
    );

    if (!filtered.length) {
      return [];
    }

    return filtered;
  };

  const handleSearchShops = async (items, keyword) => {
    const index = ++indexRef.current;
    setMultiselectShopsLoading(true);
    const newOptions = await searchShops(items, keyword);
    if (indexRef.current === index) {
      setMultiselectShopsOptions(newOptions);
      setMultiselectShopsLoading(false);
    }
  };

  const onFormSubmit = async (data) => {
    try {
      const checkActiveCampaign =
        await mutationCheckActiveCampaign.mutateAsync(data);

      if (checkActiveCampaign.data) {
        toast.error('Já existe uma campanha ativa em curso');
        return;
      }

      const adherentShops = data.adherentShops;
      delete data.adherentShops;

      data.minEntryAmount = `${data.minEntryAmount}`;

      const { data: newCampaign } =
        await mutationCreateCampaign.mutateAsync(data);

      const adherentShopsChunks = chunk(
        adherentShops.map((shop) => ({
          shopId: shop,
          campaignId: newCampaign.ROWID,
        })),
        200,
      );

      for (const chunk of adherentShopsChunks) {
        await mutationCreateCampaignAdherentShops.mutateAsync(chunk);
      }

      toast.success('Campanha criada com sucesso!');

      navigate(`/backoffice/campaigns/${newCampaign.ROWID}`);
    } catch (err) {
      toast.error('Erro ao criar campanha');
    }
  };

  const onFormSubmitError = (errors, e) => {
    toast.error(`Erro ao submeter formulário:`, {
      description: Object.keys(errors).length
        ? Object.keys(errors)
            .map((item) => errors[item].message)
            .join('; ')
        : 'Erro não identificado',
    });
  };

  const formCreateCampaignWatchNameField = formCreateCampaign.watch('name');

  return (
    <FormUnsavedConfirmationWrapper form={formCreateCampaign}>
      <Heading className="pb-6">Criar Campanha</Heading>

      <Form {...formCreateCampaign}>
        <form
          onSubmit={formCreateCampaign.handleSubmit(
            onFormSubmit,
            onFormSubmitError,
          )}
        >
          <div className="flex flex-col space-y-10">
            <div className="flex flex-col gap-5">
              <h2 className="text-xl font-medium">Dados Gerais</h2>

              <FormCard>
                <FormField
                  control={formCreateCampaign.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem className="col-span-1 lg:col-span-2">
                      <FormLabel required={true}>Nome</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          onChange={(e) => {
                            formCreateCampaign.setValue('name', e.target.value);
                            formCreateCampaign.setValue(
                              'link',
                              campaignUrl(e.target.value),
                            );
                          }}
                        />
                      </FormControl>

                      <FormDescription>
                        O nome da campanha tem de ser único
                      </FormDescription>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formCreateCampaign.control}
                  name="startDate"
                  render={({ field }) => {
                    let selectedDate = null;

                    if (field?.value?.match(REGEX_DATETIME_EU)) {
                      const [dateStr, hoursStr] = field.value.split(' ');
                      const [day, month, year] = dateStr.split('-');
                      const [hours, minutes, seconds] = hoursStr.split(':');

                      selectedDate = new Date(
                        `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`,
                      );
                    }

                    return (
                      <FormItem>
                        <FormLabel required={true} className="text-left">
                          Data de Início
                        </FormLabel>
                        <FormControl>
                          <DateTimeInputWithDatePicker
                            input={{
                              ...field,
                              placeholder: 'dd-MM-yyyy HH:mm:ss',
                            }}
                            calendar={{
                              selected: selectedDate,
                              onSelect: (val) => {
                                field.onChange(
                                  format(new Date(val), 'dd-MM-yyyy HH:mm:ss'),
                                );
                              },
                            }}
                          />
                        </FormControl>

                        <FormDescription>
                          Data de Início tem de seguir o formato:{' '}
                          <i>dd-MM-yyyy HH:mm:ss</i>
                        </FormDescription>

                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />

                <FormField
                  control={formCreateCampaign.control}
                  name="closeDate"
                  render={({ field }) => {
                    let selectedDate = null;

                    if (field?.value?.match(REGEX_DATETIME_EU)) {
                      const [dateStr, hoursStr] = field.value.split(' ');
                      const [day, month, year] = dateStr.split('-');
                      const [hours, minutes, seconds] = hoursStr.split(':');

                      selectedDate = new Date(
                        `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`,
                      );
                    }

                    return (
                      <FormItem>
                        <FormLabel required={true} className="text-left">
                          Data de Fecho de Participações
                        </FormLabel>
                        <FormControl>
                          <DateTimeInputWithDatePicker
                            input={{
                              ...field,
                              placeholder: 'dd-MM-yyyy HH:mm:ss',
                            }}
                            calendar={{
                              selected: selectedDate,
                              onSelect: (val) => {
                                field.onChange(
                                  format(new Date(val), 'dd-MM-yyyy HH:mm:ss'),
                                );
                              },
                            }}
                          />
                        </FormControl>

                        <FormDescription>
                          Data de Fecho de Participações tem de seguir o
                          formato: <i>dd-MM-yyyy HH:mm:ss</i>
                        </FormDescription>

                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />

                <FormField
                  control={formCreateCampaign.control}
                  name="endDate"
                  render={({ field }) => {
                    let selectedDate = null;

                    if (field?.value?.match(REGEX_DATETIME_EU)) {
                      const [dateStr, hoursStr] = field.value.split(' ');
                      const [day, month, year] = dateStr.split('-');
                      const [hours, minutes, seconds] = hoursStr.split(':');

                      selectedDate = new Date(
                        `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`,
                      );
                    }

                    return (
                      <FormItem>
                        <FormLabel required={true} className="text-left">
                          Data de Fim
                        </FormLabel>
                        <FormControl>
                          <DateTimeInputWithDatePicker
                            input={{
                              ...field,
                              placeholder: 'dd-MM-yyyy HH:mm:ss',
                            }}
                            calendar={{
                              selected: selectedDate,
                              onSelect: (val) => {
                                field.onChange(
                                  format(new Date(val), 'dd-MM-yyyy HH:mm:ss'),
                                );
                              },
                            }}
                          />
                        </FormControl>

                        <FormDescription>
                          Data de Fim tem de seguir o formato:{' '}
                          <i>dd-MM-yyyy HH:mm:ss</i>
                        </FormDescription>

                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />

                <FormField
                  control={formCreateCampaign.control}
                  name="numOfWinners"
                  render={({ field }) => {
                    return (
                      <FormItem>
                        <FormLabel>Nº de Vencedores</FormLabel>
                        <FormControl>
                          <Input {...field} step="1" type="number" />
                        </FormControl>

                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />

                <FormField
                  control={formCreateCampaign.control}
                  name="numOfSubstituteWinners"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nº de Suplentes</FormLabel>
                      <FormControl>
                        <Input {...field} step="1" type="number" />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formCreateCampaign.control}
                  name="minEntryAmount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required={true}>
                        Valor Minimo de Participação (€)
                      </FormLabel>
                      <FormControl>
                        <InputCurrency {...field} />
                        {/* <Input step="0.01" type="number" {...field} /> */}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formCreateCampaign.control}
                  name="legalPhrase"
                  render={({ field }) => (
                    <FormItem className="col-span-1 lg:col-span-2">
                      <FormLabel>Frase Legal</FormLabel>

                      <FormControl>
                        <Textarea {...field} />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formCreateCampaign.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem className="col-span-1 lg:col-span-2">
                      <FormLabel required={true}>
                        Condições da Campanha
                      </FormLabel>

                      <FormControl>
                        <RichTextEditor
                          value={field.value}
                          onChange={field.onChange}
                        />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formCreateCampaign.control}
                  name="regulationFile"
                  render={({ field }) => (
                    <FormItem className="col-span-1 lg:col-span-2">
                      <FormLabel required={true}>
                        Regulamento (Ficheiro)
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="file"
                          onChange={(ev) => {
                            const files = ev.target.files;
                            if (!files?.length) return;
                            const fileName = files[0].name;
                            formCreateCampaign.setValue(
                              'regulationFile',
                              files[0],
                            );
                            formCreateCampaign.setValue(
                              'regulationFilename',
                              fileName,
                            );
                          }}
                        />
                      </FormControl>
                      <FormDescription>
                        O ficheiro do regulamento tem de ser um dos seguinte
                        tipo: pdf
                      </FormDescription>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formCreateCampaign.control}
                  name="rules"
                  render={({ field }) => (
                    <FormItem className="col-span-1 lg:col-span-2">
                      <FormLabel required={true}>
                        Regulamento (Descrição)
                      </FormLabel>

                      <FormControl>
                        <RichTextEditor
                          value={field.value}
                          onChange={field.onChange}
                        />
                      </FormControl>

                      <FormDescription>
                        Para criar um link que redirecionar para o ficheiro de
                        regulamento é necessário colocar o seguinte valor no
                        link: <b>$ficheiro_regulamento</b>
                      </FormDescription>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formCreateCampaign.control}
                  name="adherentShops"
                  render={({ field }) => (
                    <FormItem className="col-span-1 lg:col-span-2">
                      <FormLabel required={true}>Lojas Aderentes</FormLabel>
                      <FormControl>
                        <div className="flex flex-col items-start gap-2 xl:flex-row xl:items-center">
                          <MultiSelect
                            disabled={formCreateCampaign.formState.isSubmitting}
                            value={field.value}
                            onSearch={(val) => {
                              handleSearchShops(queryShops.data, val);
                            }}
                            onValueChange={(values) => {
                              field.onChange(values);
                            }}
                          >
                            <MultiSelectTrigger>
                              <MultiSelectValue maxDisplay={20} />
                            </MultiSelectTrigger>
                            <MultiSelectContent>
                              <MultiSelectList>
                                <MultiSelectSearch />
                                {queryShops.isLoading ||
                                queryShops.isPending ||
                                queryShops.isRefetching ||
                                queryShops.isError ? (
                                  null || multiselectShopsOptions.length !== 0
                                ) : (
                                  <>
                                    {renderMultiSelectOptions(
                                      multiselectShopsOptions,
                                    )}
                                  </>
                                )}
                                <MultiSelectEmpty>
                                  {!multiselectShopsOptions.length ? (
                                    'Não foram encontrados resultados'
                                  ) : queryShops.isLoading ||
                                    queryShops.isRefetching ||
                                    multiselectShopsLoading ||
                                    (queryShops.status === 'pending' &&
                                      queryShops.isPending) ? (
                                    <Loading />
                                  ) : (
                                    'Não foram encontrados resultados'
                                  )}
                                </MultiSelectEmpty>
                              </MultiSelectList>
                            </MultiSelectContent>
                          </MultiSelect>

                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() => {
                              formCreateCampaign.setValue(
                                'adherentShops',
                                queryShops.data.map((item) => item.value),
                              );
                            }}
                          >
                            <CopyCheckIcon />
                            Selecionar todas as Lojas
                          </Button>
                        </div>
                      </FormControl>

                      <FormDescription>
                        As lojas cujas faturas serão válidas para gerar cupões
                        de participação na campanha
                      </FormDescription>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formCreateCampaign.control}
                  name="adherentShopsVisible"
                  render={({ field }) => (
                    <FormItem className="col-span-1 lg:col-span-2">
                      <div className="flex flex-row items-center space-x-2">
                        <FormControl>
                          <Checkbox
                            className="mr-1 size-5"
                            checked={field.value}
                            onCheckedChange={(checked) =>
                              field.onChange(checked)
                            }
                          />
                        </FormControl>
                        <FormLabel className="ml-4 space-y-0 font-normal">
                          Lojas Aderentes visivéis no Portal do Cliente?
                        </FormLabel>
                      </div>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formCreateCampaign.control}
                  name="showOrderedWinners"
                  render={({ field }) => (
                    <FormItem className="col-span-1 lg:col-span-2">
                      <div className="flex flex-row items-center space-x-2">
                        <FormControl>
                          <Checkbox
                            className="mr-1 size-5"
                            checked={field.value}
                            onCheckedChange={(checked) =>
                              field.onChange(checked)
                            }
                          />
                        </FormControl>
                        <FormLabel className="ml-4 space-y-0 font-normal">
                          Vencedores da Campanha serão apresentados por ordem
                          sequencial? (1º, 2º, 3º)
                        </FormLabel>
                      </div>

                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FormCard>
            </div>

            <div className="flex flex-col gap-5">
              <h2 className="text-xl font-medium">Página da Campanha</h2>

              <FormCard>
                <FormField
                  control={formCreateCampaign.control}
                  name="link"
                  render={({ field }) => (
                    <FormItem className="col-span-1 lg:col-span-2">
                      <FormLabel>Link</FormLabel>
                      <FormControl>
                        <Input disabled={true} {...field} />
                      </FormControl>

                      <FormDescription>
                        <Button
                          variant="ghost"
                          type="button"
                          disabled={
                            clipboard.copied ||
                            formCreateCampaignWatchNameField === ''
                          }
                          className={cn(
                            clipboard.copied &&
                              'text-green-700 dark:!text-green-300',
                          )}
                          onClick={() => clipboard.copy(field.value)}
                        >
                          {clipboard.copied ? (
                            <>
                              <CopyCheckIcon className="size-3" />
                              <span>Link Copiado</span>
                            </>
                          ) : (
                            <>
                              <CopyIcon className="size-3" />
                              <span>Copiar</span>
                            </>
                          )}
                        </Button>
                      </FormDescription>
                    </FormItem>
                  )}
                />

                <FormField
                  control={formCreateCampaign.control}
                  name="bannerFile"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required={true}>
                        Banner ({CAMPAIGN_BANNER_DIMENSIONS.width}x
                        {CAMPAIGN_BANNER_DIMENSIONS.height})
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="file"
                          onChange={(ev) => {
                            const files = ev.target.files;
                            if (!files?.length) return;
                            const fileName = files[0].name;
                            formCreateCampaign.setValue('bannerFile', files[0]);
                            formCreateCampaign.setValue(
                              'bannerFilename',
                              fileName,
                            );
                          }}
                        />
                      </FormControl>
                      <FormDescription>
                        A imagem não pode ultrapassar os 200KB e tem de ser um
                        dos seguintes tipos: jpg, png, jpeg ou webp
                      </FormDescription>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div></div>

                <FormField
                  control={formCreateCampaign.control}
                  name="heroNotActiveSmFile"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required={true}>
                        Imagem de Campanha Inativa - Ecrãs Pequenos (
                        {CAMPAIGN_INACTIVE_HERO_SM_DIMENSIONS.width}x
                        {CAMPAIGN_INACTIVE_HERO_SM_DIMENSIONS.height})
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="file"
                          onChange={(ev) => {
                            const files = ev.target.files;
                            if (!files?.length) return;
                            const fileName = files[0].name;
                            formCreateCampaign.setValue(
                              'heroNotActiveSmFile',
                              files[0],
                            );
                            formCreateCampaign.setValue(
                              'heroNotActiveSmFilename',
                              fileName,
                            );
                          }}
                        />
                      </FormControl>
                      <FormDescription>
                        A imagem não pode ultrapassar os 200KB e tem de ser um
                        dos seguintes tipos: jpg, png, jpeg ou webp
                      </FormDescription>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formCreateCampaign.control}
                  name="heroNotActiveXlFile"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required={true}>
                        Imagem de Campanha Inativa - Ecrãs Grandes (
                        {CAMPAIGN_INACTIVE_HERO_XL_DIMENSIONS.width}x
                        {CAMPAIGN_INACTIVE_HERO_XL_DIMENSIONS.height})
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="file"
                          onChange={(ev) => {
                            const files = ev.target.files;
                            if (!files?.length) return;
                            const fileName = files[0].name;
                            formCreateCampaign.setValue(
                              'heroNotActiveXlFile',
                              files[0],
                            );
                            formCreateCampaign.setValue(
                              'heroNotActiveXlFilename',
                              fileName,
                            );
                          }}
                        />
                      </FormControl>
                      <FormDescription>
                        A imagem não pode ultrapassar os 200KB e tem de ser um
                        dos seguintes tipos: jpg, png, jpeg ou webp
                      </FormDescription>

                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FormCard>
            </div>
          </div>

          <FormFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                formCreateCampaign.reset({ ...initalFormValues });
              }}
            >
              <RotateCcwIcon />
              Reset
            </Button>

            <CancelButton />

            <Button
              type="submit"
              disabled={
                formCreateCampaign.formState.isSubmitting ||
                !formCreateCampaign.formState.isDirty
              }
              isLoading={formCreateCampaign.formState.isSubmitting}
            >
              <PlusIcon />
              Criar
            </Button>
          </FormFooter>
        </form>
      </Form>
    </FormUnsavedConfirmationWrapper>
  );
}
